// Detection rule for the large-credit alert (KAN-784). Pure — no I/O.
// Source: audit_events invoice:customer_credit_large_grant, written by
// grant_customer_credit (20261013130100) in the same transaction as the grant,
// ONLY when the amount was strictly above the tenant's credit_alert_threshold
// at that moment — so every row is one alert (no threshold re-check here; the
// threshold may have changed since). Payload: {credit_entry_id, customer_id,
// customer_name, amount, threshold, reason, bucket, actor_role}.
//
// OPERATIONAL kind (securityEventRelevance.js): hidden from the admin who made
// the grant; every other admin sees it. A billing user's grant is seen by all
// admins.

const toAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function detectLargeCreditGrant(rows) {
  return (rows || []).map((row) => ({
    id: `large_credit_grant:${row.id}`,
    kind: 'large_credit_grant',
    severity: 'medium',
    actorId: row.actor_id || null,
    headline: {
      id: 'activity.alerts.largeCreditGrant',
      values: {
        actor: row.actor_name || null,
        customer: row.payload?.customer_name || null,
        amount: toAmount(row.payload?.amount),
      },
    },
    details: {
      amount: toAmount(row.payload?.amount),
      threshold: toAmount(row.payload?.threshold),
      customerId: row.payload?.customer_id || null,
    },
    lastSeen: row.created_at,
    activity: { entityType: 'invoice', eventType: 'customer_credit_large_grant' },
  }));
}

// settings/credit_limits_raised (KAN-784 follow-up) — written by
// update_company_settings when an EFFECTIVE credit limit went UP (unset =
// $1,000). Payload {raised: {<key>: {previous, new}}}. IDENTITY-style kind
// (like mfa_policy_changed): NOT operational, so the owner who raised it still
// sees it, marked "By you" (stolen-session defense). Subject = the company.
const LIMIT_KEYS = ['credit_billing_grant_cap', 'credit_alert_threshold'];

export function detectCreditLimitsRaised(rows) {
  const events = [];
  (rows || []).forEach((row) => {
    const raised = row?.payload?.raised || {};
    const changes = LIMIT_KEYS
      .filter((k) => raised[k])
      .map((k) => ({ key: k, previous: toAmount(raised[k].previous), next: toAmount(raised[k].new) }));
    if (changes.length === 0) return;
    // Title per changed key (billing cap / alert threshold / both).
    const headlineId = changes.length > 1
      ? 'activity.alerts.creditLimitsRaised'
      : (changes[0].key === 'credit_billing_grant_cap'
        ? 'activity.alerts.creditLimitBillingRaised'
        : 'activity.alerts.creditAlertRaised');
    events.push({
      id: `credit_limits_raised:${row.id}`,
      kind: 'credit_limits_raised',
      severity: 'medium',
      actorId: row.actor_id || null,
      headline: { id: headlineId, values: { actor: row.actor_name || null } },
      details: { changes },
      lastSeen: row.created_at,
      activity: { entityType: 'settings', eventType: 'credit_limits_raised' },
    });
  });
  return events;
}
